import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, Radio, Users, Layers, ListChecks, Gamepad2, ShieldAlert, BarChart3, Mail, Bell, Settings, ScrollText, Activity, LogOut, Menu, X } from "lucide-react";
import { useApp } from "@/context/AppContext";

const NAV = [
  { to: "/admin", end: true, label: "Dashboard", icon: LayoutDashboard, tid: "admin-nav-dashboard" },
  { to: "/admin/live", label: "Live Monitor", icon: Radio, tid: "admin-nav-live" },
  { to: "/admin/participants", label: "Participants", icon: Users, tid: "admin-nav-participants" },
  { to: "/admin/categories", label: "Categories", icon: Layers, tid: "admin-nav-categories" },
  { to: "/admin/questions", label: "Questions", icon: ListChecks, tid: "admin-nav-questions" },
  { to: "/admin/control", label: "Control Center", icon: Gamepad2, tid: "admin-nav-control" },
  { to: "/admin/anticheat", label: "Anti-Cheat", icon: ShieldAlert, tid: "admin-nav-anticheat" },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3, tid: "admin-nav-analytics" },
  { to: "/admin/comms", label: "Emails & Discord", icon: Mail, tid: "admin-nav-comms" },
  { to: "/admin/announcements", label: "Announcements", icon: Bell, tid: "admin-nav-announcements" },
  { to: "/admin/logs", label: "Admin Logs", icon: ScrollText, tid: "admin-nav-logs" },
  { to: "/admin/settings", label: "Settings", icon: Settings, tid: "admin-nav-settings" },
];

export default function AdminLayout() {
  const { admin, adminLogout, config } = useApp();
  const logoUrl = config?.branding?.logo_url;
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (admin === null) navigate("/admin/login");
  }, [admin, navigate]);

  if (!admin) return <div className="min-h-screen bg-slate-100" />;

  return (
    <div className="min-h-screen bg-slate-100 flex" data-testid="admin-layout">
      <aside className={`fixed md:sticky top-0 z-50 h-screen w-64 bg-[#003366] text-white flex flex-col transition-transform duration-300 ${open ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`}>
        <div className="p-5 border-b border-white/10">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-black/30 flex items-center justify-center overflow-hidden shrink-0">
              {logoUrl ? <img src={logoUrl} alt="LostDrkc" className="w-full h-full object-contain p-1" /> : <span className="font-black font-heading text-white">L</span>}
            </div>
            <div className="font-heading font-black text-lg tracking-tight">LostDrkc <span className="text-[#FF9933]">Admin</span></div>
          </div>
          <div className="text-[10px] font-bold tracking-[0.25em] text-white/40 uppercase mt-1.5">{admin.role?.replace("_", " ")}</div>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)} data-testid={n.tid}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors ${isActive ? "bg-white/15 text-white" : "text-white/60 hover:text-white hover:bg-white/5"}`}>
              <n.icon className="w-4 h-4" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10 space-y-2">
          <Link to="/" data-testid="admin-view-site" className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-semibold text-white/60 hover:text-white transition-colors"><Activity className="w-4 h-4" /> View Site</Link>
          <button onClick={() => { adminLogout(); navigate("/admin/login"); }} data-testid="admin-logout" className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-semibold text-white/60 hover:text-red-300 transition-colors"><LogOut className="w-4 h-4" /> Logout</button>
        </div>
      </aside>
      <div className="flex-1 min-w-0">
        <div className="md:hidden sticky top-0 z-40 bg-white border-b border-slate-200 px-4 h-14 flex items-center justify-between">
          <span className="font-heading font-black">LostDrkc Admin</span>
          <button onClick={() => setOpen(!open)} data-testid="admin-mobile-menu" aria-label="Menu">{open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}</button>
        </div>
        <main className="p-5 md:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
